import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  FiAward,
  FiTrendingUp,
  FiImage,
  FiStar,
  FiGlobe,
  FiZap
} from 'react-icons/fi';
import api from '../services/api';
import { LucideIcon } from '../components/IconPicker';
import NFTMintingTest from '../components/NFTMintingTest';
import NFTMintingHistory from '../components/NFTMintingHistory';
import AICartoonify from '../components/AICartoonify';
import deploymentConfig from '../config/deployment';
import { useTranslation } from '../hooks/useTranslation';

const NFTs = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Check URL params for initial tab (for redirect from /cartoonify)
  const initialTab = searchParams.get('tab') || 'rarities';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Rarity configuration state
  const [rarities, setRarities] = useState([]);
  const [raritiesLoading, setRaritiesLoading] = useState(false);
  const [raritiesSaving, setRaritiesSaving] = useState(false);
  const [raritiesError, setRaritiesError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [availableLanguages, setAvailableLanguages] = useState([]);

  // Category defaults state
  const [categoryDefaults, setCategoryDefaults] = useState([]);
  const [categoryDefaultsLoading, setCategoryDefaultsLoading] = useState(false);

  // Dynamic categories from API
  const [categories, setCategories] = useState([]);

  // Deployment key for API calls
  const DEPLOYMENT_KEY = 'discover-flaeming';

  // Fetch available languages and categories on mount
  useEffect(() => {
    fetchLanguages();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/challenge-categories');
      if (response.data.success) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Fetch rarities and category defaults when tab changes to rarities
  useEffect(() => {
    if (activeTab === 'rarities') {
      fetchRarities();
      fetchCategoryDefaults();
    }
  }, [activeTab, currentLanguage]);

  const fetchLanguages = async () => {
    try {
      const token = localStorage.getItem('cms_accessToken');
      const response = await axios.get(
        deploymentConfig.getApiUrl(`v1/translations/${DEPLOYMENT_KEY}/languages`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const langs = response.data.languages || [];
      setAvailableLanguages(langs.filter(l => l.enabled).map(l => ({
        code: l.code,
        name: l.name
      })));
    } catch (error) {
      console.error('Failed to fetch languages:', error);
      // Fallback
      setAvailableLanguages([
        { code: 'en', name: 'English' },
        { code: 'de', name: 'German' },
        { code: 'es', name: 'Spanish' }
      ]);
    }
  };

  const fetchRarities = async () => {
    try {
      setRaritiesLoading(true);
      setRaritiesError(null);
      const token = localStorage.getItem('cms_accessToken');
      console.log('[Rarities] Fetching with token:', token ? 'present' : 'missing');
      console.log('[Rarities] API URL:', deploymentConfig.getApiUrl(`nft-config/${DEPLOYMENT_KEY}/rarities?lang=${currentLanguage}`));
      const response = await axios.get(
        deploymentConfig.getApiUrl(`nft-config/${DEPLOYMENT_KEY}/rarities?lang=${currentLanguage}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('[Rarities] Response:', response.data);
      setRarities(response.data.rarities || []);
    } catch (error) {
      console.error('Failed to fetch rarities:', error);
      setRaritiesError(error.response?.data?.message || error.message || 'Failed to fetch rarities');
    } finally {
      setRaritiesLoading(false);
    }
  };

  const handleRarityUpdate = (rarityKey, field, value) => {
    setRarities(prev => prev.map(r =>
      r.rarity_key === rarityKey ? { ...r, [field]: value } : r
    ));
  };

  const handleRarityTranslationUpdate = (rarityKey, locale, value) => {
    setRarities(prev => prev.map(r => {
      if (r.rarity_key === rarityKey) {
        return {
          ...r,
          translations: { ...r.translations, [locale]: value }
        };
      }
      return r;
    }));
  };

  const saveRarity = async (rarity) => {
    try {
      setRaritiesSaving(true);
      const token = localStorage.getItem('cms_accessToken');
      await axios.put(
        deploymentConfig.getApiUrl(`nft-config/${DEPLOYMENT_KEY}/rarities/${rarity.rarity_key}`),
        {
          difficulty: rarity.difficulty,
          color: rarity.color,
          bgColor: rarity.bg_color,
          borderColor: rarity.border_color,
          textColor: rarity.text_color,
          translations: rarity.translations
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`${rarity.name} rarity saved successfully`);
    } catch (error) {
      console.error('Failed to save rarity:', error);
      alert('Failed to save rarity configuration');
    } finally {
      setRaritiesSaving(false);
    }
  };

  // Fetch category defaults
  const fetchCategoryDefaults = async () => {
    try {
      setCategoryDefaultsLoading(true);
      const token = localStorage.getItem('cms_accessToken');
      const response = await axios.get(
        deploymentConfig.getApiUrl(`nft-config/${DEPLOYMENT_KEY}/category-defaults`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategoryDefaults(response.data.categoryDefaults || []);
    } catch (error) {
      console.error('Failed to fetch category defaults:', error);
    } finally {
      setCategoryDefaultsLoading(false);
    }
  };

  // Handle category image upload
  const handleCategoryImageUpload = async (categoryKey, type, file) => {
    if (!file) return;

    try {
      const token = localStorage.getItem('cms_accessToken');
      const formData = new FormData();
      formData.append('image', file);

      // Upload the image
      const uploadResponse = await axios.post(
        deploymentConfig.getApiUrl('nft-config/upload-category-image'),
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const imageUrl = uploadResponse.data.url;

      // Update the category default
      const updateData = type === 'cartoon'
        ? { defaultCartoonUrl: imageUrl }
        : { defaultImageUrl: imageUrl };

      await axios.put(
        deploymentConfig.getApiUrl(`nft-config/${DEPLOYMENT_KEY}/category-defaults/${categoryKey}`),
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh the list
      fetchCategoryDefaults();
      alert(`${categoryKey} ${type} image uploaded successfully`);
    } catch (error) {
      console.error('Failed to upload category image:', error);
      alert('Failed to upload image');
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.nav.nfts')}</h1>
          <p className="text-gray-600">{t('cms.nfts.subtitle')}</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('rarities')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'rarities'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiStar className="w-4 h-4" />
            Rarity Config
          </button>
          <button
            onClick={() => setActiveTab('cartoonify')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'cartoonify'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiZap className="w-4 h-4" />
            {t('cms.nfts.aiCartoonify')}
          </button>
          <button
            onClick={() => setActiveTab('minting')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'minting'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiAward className="w-4 h-4" />
            {t('cms.nfts.test')}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'history'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiTrendingUp className="w-4 h-4" />
            {t('cms.nfts.history')}
          </button>
        </nav>
      </div>

      {/* Rarity Configuration Tab */}
      {activeTab === 'rarities' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-3">
            <FiStar className="w-6 h-6 text-green-600" />
            <h2 className="text-2xl font-bold text-gray-900">NFT Rarity Configuration</h2>
          </div>

          {/* Language Selector */}
          {availableLanguages.length > 1 && (
            <div className="rounded-lg p-4 border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiGlobe className="inline w-4 h-4 mr-2" />
                Edit Translations For:
              </label>
              <select
                value={currentLanguage}
                onChange={(e) => setCurrentLanguage(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {availableLanguages.map(lang => lang && lang.code ? (
                  <option key={lang.code} value={lang.code}>
                    {lang.name || lang.code} ({lang.code.toUpperCase()})
                  </option>
                ) : null)}
              </select>
            </div>
          )}

          {/* Rarity Configurations */}
          {raritiesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-gray-600">{t('cms.common.loading')}</span>
            </div>
          ) : raritiesError ? (
            <div className="text-center py-12 bg-red-50 rounded-lg border border-red-200">
              <FiStar className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-600 font-medium">Error loading rarity configurations</p>
              <p className="text-sm text-red-500 mt-2">{raritiesError}</p>
              <button
                onClick={fetchRarities}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : rarities.length > 0 ? (
            <div className="space-y-6">
              {rarities.map(rarity => (
                <div
                  key={rarity.rarity_key || rarity.id}
                  className="bg-white rounded-xl border-2 p-6 shadow-sm"
                  style={{ borderColor: rarity.border_color || '#E5E7EB' }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: rarity.color || '#9CA3AF' }}
                      >
                        {(rarity.name || rarity.rarity_key || 'R')?.[0]?.toUpperCase() || 'R'}
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{rarity.name || rarity.rarity_key || 'Unknown'}</h4>
                        <p className="text-sm text-gray-500">
                          {rarity.rarity_key || 'unknown'} • {rarity.difficulty || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => saveRarity(rarity)}
                      disabled={raritiesSaving}
                      className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
                    >
                      {raritiesSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>

                  {/* Color Pickers */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Main Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={rarity.color}
                          onChange={(e) => handleRarityUpdate(rarity.rarity_key, 'color', e.target.value)}
                          className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={rarity.color}
                          onChange={(e) => handleRarityUpdate(rarity.rarity_key, 'color', e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Background</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={rarity.bg_color}
                          onChange={(e) => handleRarityUpdate(rarity.rarity_key, 'bg_color', e.target.value)}
                          className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={rarity.bg_color}
                          onChange={(e) => handleRarityUpdate(rarity.rarity_key, 'bg_color', e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Border</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={rarity.border_color}
                          onChange={(e) => handleRarityUpdate(rarity.rarity_key, 'border_color', e.target.value)}
                          className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={rarity.border_color}
                          onChange={(e) => handleRarityUpdate(rarity.rarity_key, 'border_color', e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Text</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={rarity.text_color}
                          onChange={(e) => handleRarityUpdate(rarity.rarity_key, 'text_color', e.target.value)}
                          className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={rarity.text_color}
                          onChange={(e) => handleRarityUpdate(rarity.rarity_key, 'text_color', e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Translations */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Translations</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {availableLanguages.map(lang => lang && lang.code ? (
                        <div key={lang.code}>
                          <label className="block text-xs text-gray-600 mb-1">
                            {lang.name || lang.code} ({lang.code.toUpperCase()})
                          </label>
                          <input
                            type="text"
                            value={rarity.translations?.[lang.code] || ''}
                            onChange={(e) => handleRarityTranslationUpdate(rarity.rarity_key, lang.code, e.target.value)}
                            placeholder={`${rarity.name || rarity.rarity_key} in ${lang.name || lang.code}`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      ) : null)}
                    </div>
                  </div>

                  {/* Preview Card - Matches template gallery exactly */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-600 mb-2">Preview:</p>
                    <div className="flex items-center gap-6">
                      {/* NFT Card Preview - Same dimensions as template gallery (w-36 h-48) */}
                      <div className="w-36 h-48 relative">
                        <div
                          className="w-full h-full rounded-xl shadow-lg relative overflow-hidden"
                          style={{
                            backgroundImage: 'url(https://testcms.exploreandwin.com/uploads/gallery/nft-cartoon-images/challenge_dd73087b-5a70-4fd3-8da7-82f2e76e23a5_common_flux-edit_1764012807737.png)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            border: `6px solid ${rarity.border_color || '#E5E7EB'}`
                          }}
                        >

                          {/* Rarity Badge - Upper Right Corner (2px from border) */}
                          <div className="absolute" style={{ top: '2px', right: '2px' }}>
                            <span
                              className="inline-block px-1 py-0.5 rounded-full text-[8px] font-bold text-white shadow-sm"
                              style={{ backgroundColor: rarity.color || '#9CA3AF' }}
                            >
                              {(rarity.name || rarity.rarity_key || 'RARITY')?.toUpperCase()}
                            </span>
                          </div>

                          {/* Title at bottom - left aligned with rounded top (2px from border) */}
                          <div
                            className="absolute left-0 right-0 bg-black/60 text-white px-2 py-1.5 text-left"
                            style={{
                              bottom: '2px',
                              marginLeft: '2px',
                              marginRight: '2px',
                              borderRadius: '6px 6px 0 0'
                            }}
                          >
                            <div className="text-sm font-bold truncate">Sample NFT</div>
                            <div className="text-[10px] opacity-90 truncate">Nature</div>
                            <div className="text-[8px] opacity-75 truncate mt-0.5">Berlin, Germany</div>
                          </div>
                        </div>
                      </div>

                      <div className="text-sm text-gray-600">
                        <p>This is how a <strong>{rarity.name || rarity.rarity_key || 'this'}</strong> NFT will appear.</p>
                        <p className="mt-1">The <strong>border</strong> and <strong>rarity badge</strong> colors are determined by this configuration.</p>
                        <div className="mt-3 flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: rarity.color || '#9CA3AF' }} />
                            <span className="text-xs text-gray-500">Badge</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded border-2" style={{ borderColor: rarity.border_color || '#E5E7EB' }} />
                            <span className="text-xs text-gray-500">Border</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <FiStar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No rarity configurations found.</p>
              <p className="text-sm text-gray-500 mt-2">Please ensure the deployment has rarity configs set up in the database.</p>
            </div>
          )}

          {/* Category Default Images Section */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <FiImage className="w-6 h-6 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900">Category Default Images</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Set default images for each challenge category. These will be used when a challenge has no image.
            </p>

            {categoryDefaultsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-gray-600">{t('cms.common.loading')}</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Show ALL categories, merging with any existing defaults */}
                {categories.filter(cat => cat.isEnabled).map(cat => {
                  // Find existing default images for this category
                  const defaults = categoryDefaults.find(d => d.category_key === cat.slug) || {};
                  return (
                    <div
                      key={cat.id}
                      className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: cat.color + '20' }}
                          >
                            <LucideIcon name={cat.icon} size={16} color={cat.color} />
                          </div>
                          <h4 className="text-lg font-semibold text-gray-900">{cat.name}</h4>
                        </div>
                        <span className="text-xs text-gray-500">
                          {defaults.default_image_url || defaults.default_cartoon_url ? 'Image set' : 'No image'}
                        </span>
                      </div>

                      {/* Image Preview */}
                      <div className="aspect-[4/5] bg-gray-100 rounded-lg mb-3 overflow-hidden relative">
                        {defaults.default_cartoon_url || defaults.default_image_url ? (
                          <img
                            src={`${deploymentConfig.getBaseUrl()}${defaults.default_cartoon_url || defaults.default_image_url}`}
                            alt={cat.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <FiImage className="w-12 h-12" />
                          </div>
                        )}
                      </div>

                      {/* Upload Buttons */}
                      <div className="space-y-2">
                        <label className="block">
                          <span className="text-xs text-gray-600">Default Image</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleCategoryImageUpload(cat.slug, 'image', e.target.files[0])}
                            className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 mt-1"
                          />
                        </label>
                        <label className="block">
                          <span className="text-xs text-gray-600">Default Cartoon</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleCategoryImageUpload(cat.slug, 'cartoon', e.target.files[0])}
                            className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 mt-1"
                          />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Minting Tab */}
      {activeTab === 'minting' && (
        <NFTMintingTest />
      )}

      {/* Minting History Tab */}
      {activeTab === 'history' && (
        <NFTMintingHistory />
      )}

      {/* AI Cartoonify Tab */}
      {activeTab === 'cartoonify' && (
        <AICartoonify />
      )}
    </div>
  );
};

export default NFTs;